import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, PhoneCall, MessageSquare } from "lucide-react";
import { fadeIn, cardHover } from '@/components/ui/animations';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const { toast } = useToast();

  const validateForm = (data: {name: string; email: string; subject: string; message: string}) => {
    const newErrors: {[key: string]: string} = {};
    
    if (!data.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (data.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (data.name.length > 50) {
      newErrors.name = 'Name must be less than 50 characters';
    }
    
    if (!data.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!data.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (data.subject.length > 100) {
      newErrors.subject = 'Subject must be less than 100 characters';
    }
    
    if (!data.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (data.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (data.message.length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData(e.currentTarget);
    const contactData = {
      name: (formData.get('name') as string).trim(),
      email: (formData.get('email') as string).trim(),
      subject: (formData.get('subject') as string).trim(),
      message: (formData.get('message') as string).trim(),
    };

    if (!validateForm(contactData)) return;
    
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: contactData
      });

      if (error) throw error;

      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
      });
      
      // Reset form
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`container max-w-4xl py-16 ${fadeIn}`}>
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold mb-6">Contact Us</h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <Card className={`text-center ${cardHover}`}>
          <CardContent className="pt-6">
            <Mail className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="font-semibold mb-2">Email</h3>
            <p className="text-muted-foreground">support@chargeshare.com</p>
          </CardContent>
        </Card>

        <Card className={`text-center ${cardHover}`}>
          <CardContent className="pt-6">
            <PhoneCall className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="font-semibold mb-2">Phone</h3>
            <p className="text-muted-foreground">+1 (555) 123-4567</p>
          </CardContent>
        </Card>

        <Card className={`text-center ${cardHover}`}>
          <CardContent className="pt-6">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="font-semibold mb-2">Live Chat</h3>
            <p className="text-muted-foreground">Available 24/7</p>
          </CardContent>
        </Card>
      </div>

      <Card className={cardHover}>
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl">Send us a Message</CardTitle>
          <p className="text-muted-foreground">Fill out the form below and we'll get back to you shortly.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="name">
                  Name
                </label>
                <Input 
                  id="name" 
                  name="name" 
                  placeholder="Your name" 
                  className={errors.name ? 'border-destructive' : ''}
                  maxLength={50}
                  required 
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="email">
                  Email
                </label>
                <Input 
                  id="email" 
                  name="email" 
                  type="email" 
                  placeholder="your@email.com" 
                  className={errors.email ? 'border-destructive' : ''}
                  required 
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="subject">
                Subject
              </label>
              <Input 
                id="subject" 
                name="subject" 
                placeholder="How can we help you?" 
                className={errors.subject ? 'border-destructive' : ''}
                maxLength={100}
                required 
              />
              {errors.subject && <p className="text-sm text-destructive">{errors.subject}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="message">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                placeholder="Type your message here..."
                className={`min-h-[150px] ${errors.message ? 'border-destructive' : ''}`}
                maxLength={1000}
                required
              />
              {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
            </div>

            <div className="flex justify-end">
              <Button size="lg" className="px-8" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Contact;
